import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Stack,
  alpha,
  styled,
  keyframes,
  Collapse,
} from "@mui/material";
import { TransitionGroup } from "react-transition-group";

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const ThinkingContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: "16px",
  background: alpha(theme.palette.background.paper, 0.6),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  marginBottom: theme.spacing(2),
  backdropFilter: "blur(10px)",
}));

const PhaseCard = styled(Box)(({ theme, phase }) => {
  const phaseColors = {
    thinking: theme.palette.info.main,
    working: theme.palette.primary.main,
    complete: theme.palette.success.main,
  };

  const color = phaseColors[phase] || theme.palette.grey[500];

  return {
    padding: theme.spacing(1.5),
    borderRadius: "12px",
    background: alpha(color, 0.05),
    border: `1px solid ${alpha(color, 0.2)}`,
    marginBottom: theme.spacing(1),
    animation:
      phase === "complete" ? "none" : `${pulse} 2s ease-in-out infinite`,
  };
});

function PhaseDisplay({ phase, content, details }) {
  const getPhaseLabel = () => {
    switch (phase) {
      case "thinking":
        return "Thinking";
      case "working":
        return "Working";
      case "complete":
        return "Complete";
      default:
        return "";
    }
  };

  return (
    <PhaseCard phase={phase}>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
        <Box mb={0.5}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            {getPhaseLabel()}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontSize: "13px",
            color: "text.primary",
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
          }}
        >
          {content}
        </Typography>

        {details && (
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              mt: 0.5,
              fontStyle: "italic",
              display: "block",
            }}
          >
            {details}
          </Typography>
        )}
      </Stack>
    </PhaseCard>
  );
}

export default function ThinkingModeDisplay({ progressUpdates = [] }) {
  const [phases, setPhases] = useState([]);

  useEffect(() => {
    if (!progressUpdates.length) return;

    const latest = progressUpdates[progressUpdates.length - 1];

    if (latest.type === "thinking" || latest.type === "working") {
      const newPhase = {
        id: `${latest.type}-${latest.step || Date.now()}`,
        phase: latest.type,
        content: latest.content || latest.details || "",
        details: latest.type === "working" ? latest.details : null,
        timestamp: Date.now(),
      };

      setPhases((prev) => {
        const existingIndex = prev.findIndex((p) => p.id === newPhase.id);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...newPhase };
          return updated;
        }
        return [...prev, newPhase];
      });
    }

    if (latest.type === "answer_start") {
      setPhases((prev) =>
        prev.map((p, idx) =>
          idx === prev.length - 1 ? { ...p, phase: "complete" } : p
        )
      );

      setTimeout(() => {
        setPhases([]);
      }, 2000);
    }
  }, [progressUpdates]);

  if (phases.length === 0) return null;

  return (
    <ThinkingContainer>
      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          Reasoning Process
        </Typography>

        <TransitionGroup>
          {phases.map((phase) => (
            <Collapse key={phase.id}>
              <PhaseDisplay
                phase={phase.phase}
                content={phase.content}
                details={phase.details}
              />
            </Collapse>
          ))}
        </TransitionGroup>
      </Stack>
    </ThinkingContainer>
  );
}
